import { useQuery } from '@tanstack/react-query';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import type { TableProps } from 'antd';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Link } from 'react-router-dom';
import { Project } from '../../../models/Project/Project';
import { FaEdit as EditIcon, FaTrash as DeleteIcon } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';

import style from './project-list.module.scss';
import { useMemo, useState } from 'react';

type DataType = {
  key: number;
  id: number;
  name: string;
  price: number;
  points_required: number;
  duration: number;
};

export default function ProjectList() {
  const {
    data: projects = [],
    refetch,
    isLoading
  } = useQuery(['projects'], () => Project.all());

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const initialValues: DataType[] = useMemo(
    () =>
      projects.map((project) => ({
        key: project.id,
        id: project.id,
        name: project.name,
        price: project.price,
        points_required: project.points_required,
        duration: project.duration
      })),
    [projects]
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await Project.delete(deleteTarget.id);
      toast.success(`Projekat "${deleteTarget.name}" je uspešno obrisan.`);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Greška pri brisanju projekta.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      sortOrder: sortedInfo.columnKey === 'id' && sortedInfo.order
    },
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      render: (text, row) => (
        <Link to={`/admin/projects/edit/${row.id}`} className={style.editLink}>
          {text}
        </Link>
      )
    },
    {
      title: 'Cena',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price - b.price,
      sortOrder: sortedInfo.columnKey === 'price' && sortedInfo.order,
      render: (price) => `${price} RSD`
    },
    {
      title: 'Poeni',
      dataIndex: 'points_required',
      key: 'points_required',
      sorter: (a, b) => a.points_required - b.points_required,
      sortOrder: sortedInfo.columnKey === 'points_required' && sortedInfo.order
    },
    {
      title: 'Trajanje',
      dataIndex: 'duration',
      key: 'duration',
      sorter: (a, b) => a.duration - b.duration,
      sortOrder: sortedInfo.columnKey === 'duration' && sortedInfo.order
    },
    {
      title: 'Akcije',
      key: 'actions',
      render: (_, row) => (
        <div className="cs-table-controlls">
          <Link
            to={`/admin/projects/edit/${row.id}`}
            className="cs-edit-button"
          >
            <EditIcon />
          </Link>
          <button
            className="cs-delete-button"
            onClick={() => {
              setDeleteTarget({ id: row.id, name: row.name });
              setShowDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </button>
        </div>
      )
    }
  ];

  const handleChange: TableProps<DataType>['onChange'] = (
    _pagination,
    _filters,
    sorter
  ) => {
    setSortedInfo(sorter as any);
  };

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista Projekata</h1>
      <div className={style.info}>
        <div className={style.number}>
          Trenutno imate <span>{projects.length}</span> projekata
        </div>
        <ButtonPrimary url="/admin/project/create">
          Dodaj Projekat
        </ButtonPrimary>
      </div>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={initialValues}
        className="cs-table-wrapper"
        rowKey={'id'}
        onChange={handleChange}
        pagination={false}
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete projekat "${deleteTarget.name}"?`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Otkaži"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
